import {
  ArrowLeft,
  Download,
  Forward,
  Reply,
  ReplyAll,
  Trash2,
} from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useState } from "react";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";

import type { EmailDraft } from "../ComposeEmailDialog";
import type { EmailRecipient } from "../../types";
import type { MailboxMessage, MailboxMessageRef } from "../mailTypes";
import { mailErrorKey } from "../useMailAccount";
import { forwardDraft, replyDraft } from "./mailboxDrafts";
import { MessageCrmPanel } from "./MessageCrmPanel";
import {
  useDeleteMailboxMessage,
  useMailboxAttachments,
  useMailboxMessage,
} from "./useMailbox";

const formatSize = (bytes: number, locale: string) => {
  const isSmall = bytes < 1024 * 1024;
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: isSmall ? "kilobyte" : "megabyte",
    maximumFractionDigits: isSmall ? 0 : 1,
  }).format(isSmall ? Math.max(1, bytes / 1024) : bytes / 1024 / 1024);
};

const people = (list: EmailRecipient[]) =>
  list
    .map((item) => (item.name ? `${item.name} <${item.email}>` : item.email))
    .join(", ");

/** The HTML of the message, in a frame where nothing runs; links open a new tab. */
const HtmlBody = ({ html, title }: { html: string; title: string }) => (
  <iframe
    title={title}
    sandbox="allow-popups allow-popups-to-escape-sandbox"
    srcDoc={`<base target="_blank"><style>body{font-family:system-ui,sans-serif;font-size:14px;margin:12px;color:#111;background:#fff;overflow-wrap:anywhere}img{max-width:100%;height:auto}</style>${html}`}
    className="h-[60vh] min-h-80 w-full rounded-md border bg-white"
  />
);

interface MessageViewProps {
  messageRef: MailboxMessageRef;
  ownEmail: string;
  /** In the trash, deleting is for good. */
  isTrash: boolean;
  onCompose: (draft: EmailDraft) => void;
  onDeleted: () => void;
  /** Back to the list, on a phone. */
  onBack?: () => void;
}

export const MessageView = ({
  messageRef,
  ownEmail,
  isTrash,
  onCompose,
  onDeleted,
  onBack,
}: MessageViewProps) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { data: message, isPending, error } = useMailboxMessage(messageRef);
  const remove = useDeleteMailboxMessage();
  const { download, asAttachments } = useMailboxAttachments();
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isForwarding, setForwarding] = useState(false);

  if (isPending) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {translate("crm.mailbox.loading")}
      </p>
    );
  }
  if (error || !message) {
    return (
      <p className="p-4 text-sm text-destructive">
        {translate(mailErrorKey(error))}
      </p>
    );
  }

  const date = new Intl.DateTimeFormat(locale, {
    dateStyle: "full",
    timeStyle: "short",
  }).format(new Date(message.date));
  const senderName = message.from?.name || message.from?.email || "";

  const reply = (all: boolean) =>
    onCompose(
      replyDraft(message, {
        all,
        ownEmail,
        header: translate("crm.mailbox.quote_header", {
          date,
          name: senderName,
        }),
      }),
    );

  const forward = async (current: MailboxMessage) => {
    setForwarding(true);
    const attachments = await asAttachments(
      current,
      current.attachments.map((file) => file.index),
    );
    setForwarding(false);
    onCompose(
      forwardDraft(current, {
        attachments,
        header: [
          translate("crm.mailbox.forwarded"),
          `${translate("crm.mailbox.from")} : ${people(current.from ? [current.from] : [])}`,
          `${translate("crm.mailbox.date")} : ${date}`,
          `${translate("crm.mail.compose.subject")} : ${current.subject ?? ""}`,
          `${translate("crm.mail.compose.to")} : ${people(current.to)}`,
        ].join("\n"),
      }),
    );
  };

  return (
    <article className="flex flex-col gap-4 rounded-md border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        {onBack ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label={translate("ra.action.back")}
          >
            <ArrowLeft className="size-4" />
          </Button>
        ) : null}
        <Button variant="outline" size="sm" onClick={() => reply(false)}>
          <Reply className="size-4" />
          {translate("crm.mailbox.reply")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => reply(true)}>
          <ReplyAll className="size-4" />
          {translate("crm.mailbox.reply_all")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={isForwarding}
          onClick={() => forward(message)}
        >
          <Forward className="size-4" />
          {translate("crm.mailbox.forward")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto text-destructive hover:text-destructive"
          onClick={() => setConfirmOpen(true)}
        >
          <Trash2 className="size-4" />
          {translate("crm.mailbox.delete")}
        </Button>
      </div>

      <header className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold break-words">
          {message.subject || translate("crm.mail.emails.no_subject")}
        </h2>
        <p className="text-sm">
          <span className="font-medium">{senderName}</span>
          {message.from?.name ? (
            <span className="text-muted-foreground">
              {` <${message.from.email}>`}
            </span>
          ) : null}
        </p>
        <p className="text-xs text-muted-foreground">{date}</p>
        <p className="text-xs text-muted-foreground break-words">
          {translate("crm.mailbox.to_recipients", {
            recipients: people(message.to),
          })}
        </p>
        {message.cc.length > 0 ? (
          <p className="text-xs text-muted-foreground break-words">
            {translate("crm.mail.emails.cc", {
              recipients: people(message.cc),
            })}
          </p>
        ) : null}
      </header>

      <MessageCrmPanel message={message} ownEmail={ownEmail} />

      {message.html ? (
        <HtmlBody html={message.html} title={message.subject ?? "email"} />
      ) : (
        <div className="whitespace-pre-line text-sm break-words">
          {message.text}
        </div>
      )}

      {message.attachments.length > 0 ? (
        <ul
          className="flex flex-wrap gap-2"
          aria-label={translate("crm.mailbox.attachments")}
        >
          {message.attachments.map((file) => (
            <li key={file.index}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => download(message, file.index)}
                aria-label={translate("crm.mailbox.download", {
                  name: file.filename,
                })}
              >
                <Download className="size-4" />
                {file.filename}
                <span className="text-xs text-muted-foreground">
                  {formatSize(file.size, locale)}
                </span>
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      <Confirm
        isOpen={isConfirmOpen}
        title="crm.mailbox.delete"
        content={
          isTrash
            ? "crm.mailbox.delete_forever_confirm"
            : "crm.mailbox.delete_confirm"
        }
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(message, {
            onSuccess: () => {
              setConfirmOpen(false);
              onDeleted();
            },
          })
        }
        onClose={() => setConfirmOpen(false)}
      />
    </article>
  );
};
